// src/components/admin/ContactManager.tsx
// Komponen admin untuk mengelola konten kontak

import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Mail, Phone, MapPin, Clock, MessageSquare, Youtube, Instagram } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from './Toast';
import { ImageUpload } from './ImageUpload';
import {
  DEFAULT_SOCIAL_LINKS,
  fetchSocialLinksFromDB,
  saveSocialLinksToDB,
} from '../../hooks/useSocialLinks';

interface ContactInfo {
  id?: string;
  profile_id?: string;
  email?: string;
  phone?: string;
  address?: string;
  working_hours?: string;
  response_time?: string;
  social_links?: Record<string, string>;
  about_points?: string[];
  form_subjects?: string[];
  photo_url?: string;
  is_active: boolean;
}

interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied';
  created_at: string;
}

const ContactManager = () => {
  const { success, error } = useToast();
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phone: '',
    address: '',
    working_hours: '',
    response_time: '',
    social_links: {},
    about_points: [],
    form_subjects: [],
    photo_url: '',
    is_active: true
  });
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'messages'>('info');
  const [newAboutPoint, setNewAboutPoint] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');

  useEffect(() => {
    loadContactInfo();
    loadMessages();
  }, []);

  const loadContactInfo = async () => {
    try {
      setLoading(true);
      const [{ data, error: fetchError }, socialData] = await Promise.all([
        supabase
          .from('contact_info')
          .select('*')
          .eq('is_active', true)
          .maybeSingle(),
        fetchSocialLinksFromDB(),
      ]);

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const baseLinks = data?.social_links || {};
      const mergedLinks: Record<string, string> = {
        ...baseLinks,
        YouTube: baseLinks.YouTube || baseLinks.youtube || socialData.youtube || DEFAULT_SOCIAL_LINKS.youtube,
        TikTok: baseLinks.TikTok || baseLinks.tiktok || socialData.tiktok || DEFAULT_SOCIAL_LINKS.tiktok,
        Instagram: baseLinks.Instagram || baseLinks.instagram || socialData.instagram || DEFAULT_SOCIAL_LINKS.instagram,
      };

      if (data) {
        setContactInfo({
          ...data,
          social_links: mergedLinks,
        });
      } else {
        setContactInfo((prev) => ({
          ...prev,
          social_links: mergedLinks,
        }));
      }
    } catch (err) {
      console.error('Error loading contact info:', err);
      error('Gagal memuat informasi kontak');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setMessages(data || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      error('Gagal memuat pesan kontak');
    }
  };

  const handleInputChange = (field: keyof ContactInfo, value: string | boolean | Record<string, string> | string[]) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Try to find existing profile by email first, then by user_id if available
      let { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .or(`email.eq.${user.email},user_id.eq.${user.id}`)
        .single();

      // Create profile if it doesn't exist
      if (!profile) {
        const profileData: any = {
          name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Admin',
          email: user.email
        };
        
        // Add user_id only if the column exists (try-catch to handle missing column)
        try {
          profileData.user_id = user.id;
        } catch {
          // Column doesn't exist, continue without it
        }

        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert([profileData])
          .select('id')
          .single();

        if (profileError) throw profileError;
        profile = newProfile;
      }

      const dataToSave = {
        ...contactInfo,
        profile_id: profile.id
      };

      if (contactInfo.id) {
        const { error: updateError } = await supabase
          .from('contact_info')
          .update(dataToSave)
          .eq('id', contactInfo.id);

        if (updateError) throw updateError;
      } else {
        const { data: newContactInfo, error: insertError } = await supabase
          .from('contact_info')
          .insert([dataToSave])
          .select()
          .single();

        if (insertError) throw insertError;
        setContactInfo(newContactInfo);
      }

      // Sinkronkan YouTube, TikTok, Instagram ke profiles & localStorage
      const linksObj = contactInfo.social_links || {};
      await saveSocialLinksToDB({
        youtube: linksObj.YouTube || linksObj.youtube || DEFAULT_SOCIAL_LINKS.youtube,
        tiktok: linksObj.TikTok || linksObj.tiktok || DEFAULT_SOCIAL_LINKS.tiktok,
        instagram: linksObj.Instagram || linksObj.instagram || DEFAULT_SOCIAL_LINKS.instagram,
      });

      success('Informasi kontak & sosial media berhasil disimpan!');
    } catch (err) {
      console.error('Error saving contact info:', err);
      error('Gagal menyimpan informasi kontak');
    } finally {
      setSaving(false);
    }
  };

  const addAboutPoint = () => {
    if (newAboutPoint.trim()) {
      const updatedPoints = [...(contactInfo.about_points || []), newAboutPoint.trim()];
      handleInputChange('about_points', updatedPoints);
      setNewAboutPoint('');
    }
  };

  const removeAboutPoint = (index: number) => {
    const updatedPoints = (contactInfo.about_points || []).filter((_, i) => i !== index);
    handleInputChange('about_points', updatedPoints);
  };

  const addSubject = () => {
    if (newSubject.trim()) {
      const updatedSubjects = [...(contactInfo.form_subjects || []), newSubject.trim()];
      handleInputChange('form_subjects', updatedSubjects);
      setNewSubject('');
    }
  };

  const removeSubject = (index: number) => {
    const updatedSubjects = (contactInfo.form_subjects || []).filter((_, i) => i !== index);
    handleInputChange('form_subjects', updatedSubjects);
  };

  const addSocialLink = () => {
    if (newSocialPlatform.trim() && newSocialUrl.trim()) {
      const updatedLinks = {
        ...(contactInfo.social_links || {}),
        [newSocialPlatform.trim()]: newSocialUrl.trim()
      };
      handleInputChange('social_links', updatedLinks);
      setNewSocialPlatform('');
      setNewSocialUrl('');
    }
  };

  const removeSocialLink = (platform: string) => {
    const updatedLinks = { ...(contactInfo.social_links || {}) };
    delete updatedLinks[platform];
    handleInputChange('social_links', updatedLinks);
  };

  const updateMessageStatus = async (messageId: string, status: 'read' | 'replied') => {
    try {
      const { error: updateError } = await supabase
        .from('contact_messages')
        .update({ status })
        .eq('id', messageId);

      if (updateError) throw updateError;
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status } : msg
      ));
      
      success(`Pesan ditandai sebagai ${status === 'read' ? 'dibaca' : 'dibalas'}`);
    } catch (err) {
      console.error('Error updating message status:', err);
      error('Gagal memperbarui status pesan');
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!confirm('Apakah Anda yakin ingin menghapus pesan ini?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', messageId);

      if (deleteError) throw deleteError;
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      success('Pesan berhasil dihapus!');
    } catch (err) {
      console.error('Error deleting message:', err);
      error('Gagal menghapus pesan');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-300 rounded w-48"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-300 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Informasi Kontak
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'messages'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pesan Masuk ({messages.filter(m => m.status === 'unread').length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'info' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Kelola Informasi Kontak</h2>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Contact Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Informasi Dasar</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={contactInfo.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Telepon
                    </label>
                    <input
                      type="tel"
                      value={contactInfo.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+62 812-3456-7890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Alamat
                    </label>
                    <textarea
                      value={contactInfo.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Alamat lengkap"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Jam Kerja
                    </label>
                    <input
                      type="text"
                      value={contactInfo.working_hours || ''}
                      onChange={(e) => handleInputChange('working_hours', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Senin - Jumat, 09:00 - 17:00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Waktu Respon
                    </label>
                    <input
                      type="text"
                      value={contactInfo.response_time || ''}
                      onChange={(e) => handleInputChange('response_time', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Respon dalam 24 jam"
                    />
                  </div>

                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Profil
                    </label>
                    <ImageUpload
                      value={contactInfo.photo_url}
                      onChange={(url) => handleInputChange('photo_url', url)}
                      bucket="portfolio-images"
                      folder="contacts"
                      maxSize={5}
                      placeholder="Upload foto profil"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Dynamic Content */}
                <div className="space-y-6">
                  {/* About Points */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tentang Saya (Poin-poin)</h3>
                    <div className="space-y-2 mb-3">
                      {(contactInfo.about_points || []).map((point, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">{point}</span>
                          <button
                            onClick={() => removeAboutPoint(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newAboutPoint}
                        onChange={(e) => setNewAboutPoint(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Tambah poin tentang diri"
                        onKeyPress={(e) => e.key === 'Enter' && addAboutPoint()}
                      />
                      <button
                        onClick={addAboutPoint}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Form Subjects */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Subjek Formulir</h3>
                    <div className="space-y-2 mb-3">
                      {(contactInfo.form_subjects || []).map((subject, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm">{subject}</span>
                          <button
                            onClick={() => removeSubject(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newSubject}
                        onChange={(e) => setNewSubject(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Tambah subjek formulir"
                        onKeyPress={(e) => e.key === 'Enter' && addSubject()}
                      />
                      <button
                        onClick={addSubject}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Media Sosial Vano</h3>
                    <div className="space-y-3 mb-4 bg-blue-50/50 p-3.5 rounded-lg border border-blue-100">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          <Youtube className="inline h-3.5 w-3.5 mr-1 text-red-600" />
                          YouTube Vano
                        </label>
                        <input
                          type="text"
                          value={contactInfo.social_links?.YouTube || ''}
                          onChange={(e) =>
                            handleInputChange('social_links', {
                              ...(contactInfo.social_links || {}),
                              YouTube: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="https://www.youtube.com/@evano.rama"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          <span className="inline-block font-bold mr-1">♪</span>
                          TikTok Vano
                        </label>
                        <input
                          type="text"
                          value={contactInfo.social_links?.TikTok || ''}
                          onChange={(e) =>
                            handleInputChange('social_links', {
                              ...(contactInfo.social_links || {}),
                              TikTok: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="https://www.tiktok.com/@evano.rama"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          <Instagram className="inline h-3.5 w-3.5 mr-1 text-pink-600" />
                          Instagram Vano
                        </label>
                        <input
                          type="text"
                          value={contactInfo.social_links?.Instagram || ''}
                          onChange={(e) =>
                            handleInputChange('social_links', {
                              ...(contactInfo.social_links || {}),
                              Instagram: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          placeholder="https://instagram.com/evano.rama"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      {Object.entries(contactInfo.social_links || {}).map(([platform, url]) => (
                        <div key={platform} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div className="text-sm truncate pr-2">
                            <span className="font-medium">{platform}:</span> {url}
                          </div>
                          <button
                            onClick={() => removeSocialLink(platform)}
                            className="text-red-600 hover:text-red-800 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newSocialPlatform}
                          onChange={(e) => setNewSocialPlatform(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Platform lain (LinkedIn, dll)"
                        />
                        <input
                          type="url"
                          value={newSocialUrl}
                          onChange={(e) => setNewSocialUrl(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="URL"
                        />
                        <button
                          onClick={addSocialLink}
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center pt-4 border-t border-gray-200">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={contactInfo.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Aktif
                </label>
              </div>
            </div>
          ) : (
            /* Messages Tab */
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Pesan Masuk</h2>
              
              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Belum ada pesan masuk</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`border rounded-lg p-4 ${
                        message.status === 'unread' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{message.name}</h3>
                          <p className="text-sm text-gray-600">{message.email}</p>
                          {message.phone && (
                            <p className="text-sm text-gray-600">{message.phone}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            message.status === 'unread' ? 'bg-blue-100 text-blue-800' :
                            message.status === 'read' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {message.status === 'unread' ? 'Belum Dibaca' :
                             message.status === 'read' ? 'Dibaca' : 'Dibalas'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(message.created_at).toLocaleDateString('id-ID', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Subjek: {message.subject}</p>
                        <p className="text-gray-700">{message.message}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {message.status === 'unread' && (
                          <button
                            onClick={() => updateMessageStatus(message.id, 'read')}
                            className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                          >
                            Tandai Dibaca
                          </button>
                        )}
                        {message.status !== 'replied' && (
                          <button
                            onClick={() => updateMessageStatus(message.id, 'replied')}
                            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            Tandai Dibalas
                          </button>
                        )}
                        <button
                          onClick={() => deleteMessage(message.id)}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Hapus
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactManager;